import { TOKEN_STORAGE_KEY } from "@saleor/auth";
import {
  DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
} from "@saleor/config";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import FulfillmentPageDialogs from "@saleor/masterFulfillment/components/FulfillmentPageDialogs/FulfillmentPageDialogs";
import MasterFulfillmentPage from "@saleor/masterFulfillment/components/MasterFulfillmentPage";
import SelectDeliveryTypeDialog from "@saleor/masterFulfillment/components/SelectDeliveryTypeDialog";
import { maybe } from "@saleor/misc";
import {
  useRefreshGrabExpressStatus,
  useRefreshPandaGoAutoStatus
} from "@saleor/motherOrders/mutations";
import {
  useMallFilterData,
  useOrderListQuery
} from "@saleor/motherOrders/queries";
import { OrderCourierEnum } from "@saleor/motherOrders/utils/enums";
import useMallSearch from "@saleor/searches/useMallSearch";
import { ListViews } from "@saleor/types";
import {
  DeliveryTypeEnum,
  OrderDirection,
  OrderSortField,
  SmopOrderStatusEnum,
  SmopStatusEnum
} from "@saleor/types/globalTypes";
import { getGteLteVariables } from "@saleor/utils/filters";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { canViewAllMalls } from "@saleor/utils/handlers/userHandler";
import { getSortParams } from "@saleor/utils/sort";
import React, { useEffect, useState } from "react";

import {
  masterFulfillmentListUrl,
  masterFulfillmentUrl,
  OrderListUrlDialog,
  OrderListUrlQueryParams
} from "../../../motherOrders/urls";
import {
  areFiltersApplied,
  getFilterOpts,
  getFilterOptsWithMall,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables
} from "../../../motherOrders/views/OrderList/filters";
import {
  getDefaultTab,
  getMoment,
  getUrlParam,
  hasAppliedFilters
} from "../../../motherOrders/views/OrderList/handlers";
import { getSortQueryVariables } from "../../../motherOrders/views/OrderList/sort";

interface MasterFulfillmentProps {
  params: OrderListUrlQueryParams;
}

export const MasterFulfillment: React.FC<MasterFulfillmentProps> = ({
  params
}) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const { updateListSettings, settings } = useListSettings(
    ListViews.ORDER_LIST
  );
  const today = getMoment("YYYY-MM-DD");
  const month = getMoment("YYYY-MM-DD", -31);

  const paginationState = createPaginationState(settings.rowNumber, params);
  const currencySymbol = "PHP";

  const optionsDelTypes = [
    { label: "Express Delivery", value: DeliveryTypeEnum.EXPRESS },
    {
      label: "10km Express Delivery",
      value: DeliveryTypeEnum.EXPRESS_LONG_DISTANCE
    },
    { label: "Same Day Delivery", value: DeliveryTypeEnum.SAME_DAY },
    { label: "Standard Delivery", value: DeliveryTypeEnum.NEXT_DAY },
    {
      label: "Nationwide Delivery",
      value: DeliveryTypeEnum.NATIONWIDE_DELIVERY
    }
  ];

  const getStorageDelTypes = (): string[] => {
    const storageDelTypesString = sessionStorage.getItem(
      TOKEN_STORAGE_KEY.DEL_TYPES
    );
    return JSON.parse(storageDelTypesString) || [];
  };

  const [openSelectDelTypeModal, setOpenSelectDelTypeModal] = useState(
    getStorageDelTypes().length === 0
  );
  const [selectedDelTypes, setSelectedDelTypes] = React.useState(
    getStorageDelTypes()
  );
  const [activeDelType, setActiveDelType] = React.useState("");

  useEffect(
    () =>
      navigate(
        masterFulfillmentListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  useEffect(() => {
    if (params.payment !== null) {
      params.after = null;
      params.before = null;
    }
  }, [params]);

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params, settings.rowNumber]
  );

  const checkParamsSort = () => {
    if (params.sort === "date" || params.sort === "number") {
      return getSortQueryVariables(params);
    } else {
      return {
        direction: OrderDirection.ASC,
        field: OrderSortField.CREATION_DATE
      };
    }
  };

  const createDefaultTabs = getDefaultTab(getFilterTabs);

  const handleTabChange = (tab: number) => {
    let urlParams = null;
    if (activeDelType === "") {
      delete params.deliveryType;
      urlParams = {
        ...createDefaultTabs[tab - 1].data,
        activeTab: tab.toString()
      };
    } else {
      urlParams = {
        ...createDefaultTabs[tab - 1].data,
        activeTab: tab.toString(),
        deliveryType: [activeDelType]
      };
    }

    navigate(masterFulfillmentListUrl(urlParams));
  };

  const { data, loading, refetch } = useOrderListQuery({
    displayLoader: true,
    skip: !activeDelType && selectedDelTypes.length === 0,
    variables: {
      ...queryVariables,
      filter: {
        ...getFilterVariables(params, selectedDelTypes),
        created: getGteLteVariables({
          /* Removes default date if any filter is applied */
          gte:
            !getUrlParam("createdFrom") && getUrlParam("shippingFrom")
              ? null
              : hasAppliedFilters(params)
              ? getMoment("YYYY-MM-DD", -90)
              : params.createdFrom || month,
          lte:
            !getUrlParam("createdTo") && getUrlParam("shippingTo")
              ? null
              : hasAppliedFilters(params)
              ? today
              : params.createdTo || today
        })
      },

      sort: checkParamsSort()
    }
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.orders.pageInfo),
    paginationState,
    params
  );
  const handleSort = createSortHandler(
    navigate,
    masterFulfillmentListUrl,
    params
  );
  const handleSearch = (text: string) => {
    navigate(masterFulfillmentListUrl({ ...params, query: text }));
  };
  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: masterFulfillmentListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    OrderListUrlDialog,
    OrderListUrlQueryParams
  >(navigate, masterFulfillmentListUrl, params);

  const [refreshGrabExpressStatus] = useRefreshGrabExpressStatus({
    onCompleted: data => {
      if (data.refreshGrabExpressStatus.orderErrors.length === 0) {
        refetch();
      } else {
        notify({
          status: "error",
          text: `${data.refreshGrabExpressStatus.orderErrors[0].message}`
        });
      }
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const [refreshPandaGoStatus] = useRefreshPandaGoAutoStatus({
    onCompleted: data => {
      if (data.refreshPandagoAutoStatus.orderErrors.length === 0) {
        refetch();
      } else {
        notify({
          status: "error",
          text: `${data.refreshPandagoAutoStatus.orderErrors[0].message}`
        });
      }
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  const tabs = createDefaultTabs;
  const currentTab = () => {
    if (params.activeTab === undefined) {
      return areFiltersApplied(params) ? tabs.length + 1 : 0;
    } else {
      return parseInt(params.activeTab, 10);
    }
  };

  useEffect(() => {
    if (!!activeDelType) {
      navigate(
        masterFulfillmentListUrl({ ...params, deliveryType: [activeDelType] })
      );
    } else if (activeDelType === "") {
      delete params.deliveryType;
      navigate(masterFulfillmentListUrl({ ...params }));
    }
  }, [activeDelType]);

  useEffect(() => {
    if (!!selectedDelTypes && selectedDelTypes.length > 0) {
      sessionStorage.setItem(
        TOKEN_STORAGE_KEY.DEL_TYPES,
        JSON.stringify(selectedDelTypes)
      );
    }
  }, [selectedDelTypes]);

  const { data: initialFilterData } = useMallFilterData({
    variables: {
      first: params?.mall && params.mall.length > 0 ? params.mall.length : 5,
      mall: params.mall
    }
  });

  const searchMall = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      filterStatus: SmopStatusEnum.ACT,
      first: 5
    }
  });

  const filterOpts = canViewAllMalls()
    ? getFilterOptsWithMall(params, {
        initial: initialFilterData?.malls?.edges?.map(edge => edge.node) || [],
        search: searchMall
      })
    : getFilterOpts(params);

  return (
    <>
      <MasterFulfillmentPage
        refetchOrders={refetch}
        brands={null}
        currencySymbol={currencySymbol}
        settings={settings}
        currentTab={currentTab()}
        disabled={loading}
        filterOpts={filterOpts}
        orders={maybe(() => data?.orders.edges.map(edge => edge.node))}
        pageInfo={pageInfo}
        sort={getSortParams(params)}
        onAdd={null}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        onRowClick={id => () => navigate(masterFulfillmentUrl(id))}
        onSort={handleSort}
        onSearchChange={handleSearch}
        onFilterChange={changeFilters}
        onTabSave={() => openModal("save-search")}
        onTabDelete={() => openModal("delete-search")}
        onTabChange={tab => handleTabChange(tab)}
        initialSearch={params.query || ""}
        tabs={createDefaultTabs.map(tab => tab.name)}
        onAll={resetFilters}
        queryVariables={queryVariables}
        activeTab={params.activeTab}
        generateShippingDialog={orderId =>
          openModal("onClickGenerateShippingLabel", { orderId })
        }
        onAssignRider={(orderId, orderCode, mallId) => {
          openModal("onClickAssignRider", { mallId, orderCode, orderId });
        }}
        onAddRemarks={(
          orderId,
          orderCode,
          orderOldRemarks,
          orderOldRemarkId
        ) => {
          if (orderOldRemarkId) {
            openModal("onClickAddRemarks", {
              orderCode,
              orderId,
              orderOldRemarkId,
              orderOldRemarks
            });
          } else {
            openModal("onClickAddRemarks", { orderCode, orderId });
          }
        }}
        onOverrideStatus={(
          orderId,
          orderStatus: SmopOrderStatusEnum,
          newOrderStatus: SmopOrderStatusEnum,
          courier
        ) => {
          if (courier === OrderCourierEnum.GRAB_EXPRESS) {
            openModal("confirmGrabOverride", {
              courier,
              newOrderStatus,
              orderId,
              orderStatus
            });
          } else {
            openModal("onOverrideStatus", {
              courier,
              newOrderStatus,
              orderId,
              orderStatus
            });
          }
        }}
        onUpdateStatus={(
          orderId,
          orderStatus: SmopOrderStatusEnum,
          newOrderStatus: SmopOrderStatusEnum,
          courier
        ) => {
          if (
            courier === OrderCourierEnum.GRAB_EXPRESS &&
            params.newOrderStatus !== SmopOrderStatusEnum.COMPLETED
          ) {
            openModal("confirmGrabOverride", {
              courier,
              newOrderStatus,
              orderId,
              orderStatus
            });
          } else {
            openModal("onClickSaveButton", {
              newOrderStatus,
              orderId,
              orderStatus
            });
          }
        }}
        onCancelStatus={(courier, orderId) =>
          openModal("cancel", { courier, orderId })
        }
        download={() => openModal("download")}
        onGrabStatusRefresh={orderId => {
          refreshGrabExpressStatus({
            variables: {
              id: orderId
            }
          });
          refetch();
        }}
        onPandaGoStatusRefresh={orderId => {
          refreshPandaGoStatus({
            variables: {
              id: orderId
            }
          });
          refetch();
        }}
        optionsDelTypes={optionsDelTypes}
        selectedDelTypes={selectedDelTypes}
        activeDelType={activeDelType}
        onClickedOpenDelTypesModal={delTypes => {
          setSelectedDelTypes(delTypes);
          setOpenSelectDelTypeModal(true);
        }}
        onActiveDelType={delType => setActiveDelType(delType)}
      />

      <SelectDeliveryTypeDialog
        open={openSelectDelTypeModal}
        onClose={() => setOpenSelectDelTypeModal(false)}
        onConfirm={(delTypes: string[]) => {
          setActiveDelType("");
          setSelectedDelTypes(delTypes);
          setOpenSelectDelTypeModal(false);
        }}
        optionsDelTypes={optionsDelTypes}
        defaultDelTypes={selectedDelTypes}
      />

      <FulfillmentPageDialogs
        params={params}
        refetch={refetch}
        openModal={openModal}
        closeModal={closeModal}
      />
    </>
  );
};

export default MasterFulfillment;
